import { useState } from "react";
import { Search } from "lucide-react";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { IssuePersonFilter } from "@/components/issues/issue-person-filter/issue-person-filter";
import { useCurrentUser } from "@/hooks/use-current-user";
import { useProjectPeople } from "@/hooks/use-project-people";
import {
  EMPTY_ISSUE_FILTERS,
  type IssueRefinementFilters,
} from "@/lib/issue-filters";

interface IssueFilterDrawerProps {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: IssueRefinementFilters;
  onApply: (filters: IssueRefinementFilters) => void;
  /**
   * Offer Assigned to and Raised by. They list the project's members, so only
   * for someone who sees every issue. See [[issues-page.spec]] § Only the
   * issues you're on.
   */
  showPeople?: boolean;
}

/**
 * Everything filterable that is not status, on mobile.
 *
 * Status stays on the page as navigation with its counts; these are
 * refinements of a view the user has already chosen. See [[issues-page.spec]].
 *
 * Edits are held locally and committed on Apply rather than written straight
 * through. Each change would otherwise refetch the list and the counts behind
 * the open drawer — four requests to set three filters, on a connection that
 * may not have them to spare. Desktop has no such problem, so
 * [[issue-filter-sidebar.spec]] writes through instead.
 */
export function IssueFilterDrawer({
  projectId,
  open,
  onOpenChange,
  value,
  onApply,
  showPeople = true,
}: IssueFilterDrawerProps) {
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[85vh]">
        {/*
          Keyed on `open` so the form REMOUNTS each time the drawer opens,
          seeding its draft from the current filters. Syncing that with an
          effect would mean setState during an effect — a cascading render, and
          a lint error the rule is right about.
        */}
        <FilterForm
          key={open ? "open" : "closed"}
          projectId={projectId}
          initial={value}
          onApply={onApply}
          onClose={() => onOpenChange(false)}
          showPeople={showPeople}
        />
      </DrawerContent>
    </Drawer>
  );
}

interface FilterFormProps {
  projectId: string;
  initial: IssueRefinementFilters;
  onApply: (filters: IssueRefinementFilters) => void;
  onClose: () => void;
  showPeople: boolean;
}

function FilterForm({
  projectId,
  initial,
  onApply,
  onClose,
  showPeople,
}: FilterFormProps) {
  const { people } = useProjectPeople(projectId);
  const { data: currentUser } = useCurrentUser();
  const [draft, setDraft] = useState<IssueRefinementFilters>(initial);

  const apply = () => {
    onApply(draft);
    onClose();
  };

  const clear = () => {
    onApply(EMPTY_ISSUE_FILTERS);
    onClose();
  };

  return (
    <>
      <DrawerHeader>
          <DrawerTitle>Filter issues</DrawerTitle>
        </DrawerHeader>

      <div className="flex flex-col gap-6 overflow-y-auto px-4 pb-4">
        <section className="flex flex-col gap-2">
          <label
            htmlFor="issue-search"
            className="text-sm font-medium text-foreground"
          >
            Search
          </label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="issue-search"
              value={draft.search}
              placeholder="Title or description"
              onChange={(event) =>
                setDraft((d) => ({ ...d, search: event.target.value }))
              }
              className="pl-9"
            />
          </div>
        </section>

        <section className="flex items-center justify-between gap-4">
          <div className="flex flex-col">
            <span className="text-sm font-medium">Overdue only</span>
            <span className="text-xs text-muted-foreground">
              Past its due date and not yet closed
            </span>
          </div>
          <Switch
            checked={draft.overdue}
            onCheckedChange={(checked) =>
              setDraft((d) => ({ ...d, overdue: checked }))
            }
            aria-label="Overdue only"
          />
        </section>

        {showPeople && (
          <>
            <IssuePersonFilter
              title="Assigned to"
              people={people}
              currentUserId={currentUser?.id}
              selected={draft.assignedTo}
              onSelect={(id) => setDraft((d) => ({ ...d, assignedTo: id }))}
            />

            <IssuePersonFilter
              title="Raised by"
              people={people}
              currentUserId={currentUser?.id}
              selected={draft.createdBy}
              onSelect={(id) => setDraft((d) => ({ ...d, createdBy: id }))}
            />
          </>
        )}
      </div>

      <div className="flex gap-2 border-t border-border p-4">
        <Button variant="outline" className="basis-[38%]" onClick={clear}>
          Clear
        </Button>
        <Button className="flex-1" onClick={apply}>
          Apply
        </Button>
      </div>
    </>
  );
}
